import { Ship, BedDouble, Car, Sun, Globe } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const INFO_BLOCKS = [
  {
    key: "getting-there",
    icon: Ship,
    title: "Getting to Sifnos",
    body:
      "Sifnos has no airport, but you can reach it by ferry from Piraeus or — for the adventurous — by helicopter.",
    bullets: [
      <>
        Conventional ferry (Blue Star Ferries) — approx. 5h, comfortable, runs
        daily.
      </>,
      <>
        High-speed ferry (SeaJets, Golden Star) — approx. 2h 30m to 3h, faster
        but pricier.
      </>,
      <>
        Book early — September is high season. Compare and book on{" "}
        <a
          href="https://www.ferryhopper.com/en"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
        >
          ferryhopper.com
        </a>{" "}
        or{" "}
        <a
          href="https://www.ferryscanner.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
        >
          ferryscanner.com
        </a>
        .
      </>,
      <>
        Arrival port: Kamares — taxis and a small KTEL bus connect to Apollonia
        and Artemonas.
      </>,
      <>
        <span className="font-medium">By helicopter:</span> a fast, scenic
        transfer from Athens or other islands — book through{" "}
        <a
          href="https://flyhoper.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
        >
          flyhoper.com
        </a>
        .
      </>,
    ],
  },
  {
    key: "where-to-stay",
    icon: BedDouble,
    title: "Where to stay",
    body:
      "Most of our venues are around Platis Gialos, so staying there will make the three days easiest. That said, Apollonia, Artemonas, Kastro and Vathi are all lovely alternatives within a short drive.",
    bullets: [
      <>
        <span className="font-medium">Platis Gialos</span> — our top
        recommendation. The beach base closest to the baptism at Chrysopigi and
        the reception at Lazarou.
      </>,
      <>
        <span className="font-medium">Apollonia</span> — the island&rsquo;s
        lively capital. Cafés, restaurants, boutiques and the main nightlife.
      </>,
      <>
        <span className="font-medium">Artemonas</span> — quieter, elegant
        village (5 min from Apollonia). Closest to our Day One dinner.
      </>,
      <>
        <span className="font-medium">Kastro</span> — historic medieval
        cliffside village with sweeping Aegean views.
      </>,
      <>
        <span className="font-medium">Vathi</span> — quiet sandy bay, lovely
        if you want a slower base.
      </>,
      <div className="mt-2">
        <p className="font-medium text-[#4A5346] mb-3">
          A few hotels we recommend in Platis Gialos
        </p>
        <ul className="space-y-2 text-[14px]">
          <li>
            <a
              href="https://www.sifnosedem.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Edem Hotel Apartments
            </a>{" "}
            —{" "}
            <span className="italic text-[#6F8175]">
              offers a 15% discount for our guests; mention &ldquo;Baptism
              Simou&rdquo; when booking.
            </span>
          </li>
          <li>
            <a
              href="https://www.sifnoshotelbenaki.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Mpenaki Hotel
            </a>
          </li>
          <li>
            <a
              href="https://www.verinahotelsifnos.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Verina Terra
            </a>
          </li>
          <li>
            <a
              href="https://www.giannakas-studios.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Giannakas Studios
            </a>
          </li>
          <li>
            <a
              href="https://www.platys-gialos.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Hotel Platys Gialos
            </a>
          </li>
          <li>
            <a
              href="https://www.alexandros-hotel-sifnos.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Alexandros Hotel Sifnos
            </a>
          </li>
          <li>
            <a
              href="https://www.turquoisesifnos.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Turquoise Hotel
            </a>
          </li>
        </ul>
      </div>,
    ],
  },
  {
    key: "getting-around",
    icon: Car,
    title: "Getting around",
    body:
      "Sifnos is small but hilly. We strongly recommend renting a car or ATV for the three days.",
    bullets: [
      "Pre-book your car well in advance — September fills up fast.",
      "ATVs / scooters are popular for solo travellers; comfortable cars are best for families.",
      "Roads are paved but winding — drive slowly, especially at night.",
      "KTEL bus connects Kamares, Apollonia, Artemonas, Platis Gialos and the main beaches.",
      <div className="mt-2">
        <p className="font-medium text-[#4A5346] mb-3">
          Recommended rental companies
        </p>
        <ul className="space-y-2 text-[14px]">
          <li>
            <a
              href="https://www.protomotocar.gr/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Protomoto Car
            </a>
          </li>
          <li>
            <a
              href="https://suntrail.gr/en/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Suntrail
            </a>
          </li>
          <li>
            <a
              href="https://sifnosdrive.gr/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346]"
            >
              Sifnos Drive
            </a>
          </li>
        </ul>
        <p className="italic text-[#6F8175] mt-3 text-[14px]">
          All rental companies are based at the port of Kamares — convenient
          right when you arrive.
        </p>
      </div>,
    ],
  },
  {
    key: "weather-dress",
    icon: Sun,
    title: "Weather & what to wear",
    body:
      "Mid-September on Sifnos is golden: warm sunny days, cool breezy evenings and the meltemi wind softening.",
    bullets: [
      "Daytime: 24–28°C — linen, cotton, light dresses, swimwear, sun hat & sunscreen.",
      "Evenings: 18–22°C — bring a light jacket, shawl or cardigan, the breeze can pick up.",
      "Baptism (Day 3): soft elegant attire — pastels, whites, light suits. The chapel is whitewashed stone, comfortable shoes for the steps.",
      "Reception at Lazarou Beach: smart resort wear. The dance floor is by the sea — flats or low heels recommended.",
    ],
  },
  {
    key: "useful-links",
    icon: Globe,
    cols: 2,
    title: "Useful Sifnos websites",
    body:
      "A handful of resources we found genuinely helpful when planning the trip — bookmark whichever ones speak to you.",
    bullets: [
      <>
        <a
          href="https://travel-to-sifnos.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          travel-to-sifnos.com
        </a>{" "}
        — the most complete English guide to the island (villages, beaches,
        hikes, restaurants).
      </>,
      <>
        <a
          href="https://www.sifnos.gr/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          sifnos.gr
        </a>{" "}
        — the official Municipality of Sifnos website (events, ferry timetable,
        emergency numbers).
      </>,
      <>
        <a
          href="https://discoversifnos.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          discoversifnos.com
        </a>{" "}
        — beautifully curated guide for boutique stays, dining and Sifnian
        culture.
      </>,
      <>
        <a
          href="https://www.ferryhopper.com/en/ferries-to/sifnos"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          ferryhopper.com / Sifnos
        </a>{" "}
        — the easiest way to compare ferry routes & buy tickets.
      </>,
      <>
        <a
          href="https://www.meteo.gr/cf-en.cfm?city_id=37"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          meteo.gr · Sifnos forecast
        </a>{" "}
        — the trusted Greek weather service, useful in the days before
        departure.
      </>,
      <>
        <a
          href="https://www.windy.com/?36.978,24.726,10"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          windy.com · Sifnos
        </a>{" "}
        — live wind & sea conditions if you'd like to check the meltemi.
      </>,
      <>
        <a
          href="https://www.aegeanthesaurus.gr/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          aegeanthesaurus.gr
        </a>{" "}
        — Sifnos travel agency: car rentals, transfers, day tours.
      </>,
      <>
        <a
          href="https://thesifnoschronicler.wordpress.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          The Sifnos Chronicler
        </a>{" "}
        — a beloved local blog with deep stories about Sifnian life, food, and
        traditions.
      </>,
      <>
        <a
          href="https://www.visitgreece.gr/islands/cyclades/sifnos/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          visitgreece.gr · Sifnos
        </a>{" "}
        — the official Greek National Tourism site with maps, culture and
        practical info.
      </>,
      <>
        <a
          href="https://thiswaytoparadise.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          thiswaytoparadise.com
        </a>{" "}
        — a beautifully written travel journal with Sifnos itineraries and tips.
      </>,
      <>
        <a
          href="https://www.ferryscanner.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          ferryscanner.com
        </a>{" "}
        — alternative ferry comparison & booking engine across Greek islands.
      </>,
      <>
        <a
          href="https://flyhoper.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 decoration-[#8FA092]/60 hover:decoration-[#6F8175] text-[#4A5346] font-medium"
        >
          flyhoper.com
        </a>{" "}
        — book a scenic helicopter transfer to Sifnos from Athens or other
        islands.
      </>,
    ],
  },
];

function InfoCard({ block, index }) {
  const ref = useReveal();
  const Icon = block.icon;
  return (
    <article
      ref={ref}
      className={`reveal glass-card rounded-[2rem] sm:rounded-[2.5rem] p-8 sm:p-10 h-full ${
        block.cols === 2 ? "md:col-span-2" : ""
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
      data-testid={`island-info-${block.key}`}
    >
      <div className="flex items-center gap-4">
        <span className="flex items-center justify-center w-12 h-12 rounded-full bg-[#E8D8CE]/60 border border-[#D4C4B7]">
          <Icon className="w-5 h-5 text-[#4A5346]" strokeWidth={1.4} />
        </span>
        <h3 className="font-serif-display text-2xl sm:text-3xl text-[#4A5346] leading-tight">
          {block.title}
        </h3>
      </div>

      <p className="text-[#757E70] leading-relaxed mt-6 text-[15px]">
        {block.body}
      </p>

      <ul
        className={`mt-6 space-y-3 ${
          block.cols === 2 ? "md:columns-2 md:gap-x-10 md:space-y-0" : ""
        }`}
      >
        {block.bullets.map((item, i) => (
          <li
            key={i}
            className={`flex gap-3 text-[15px] text-[#4A5346] leading-relaxed ${
              block.cols === 2 ? "md:break-inside-avoid md:mb-4" : ""
            }`}
          >
            <span
              aria-hidden="true"
              className="mt-[10px] block w-1.5 h-1.5 rounded-full bg-[#8FA092] shrink-0"
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function IslandInfo() {
  const headerRef = useReveal();
  return (
    <section
      id="island-info"
      className="relative py-24 sm:py-32 lg:py-40 px-6 bg-[#F2EFE9]"
      data-testid="island-info-section"
    >
      <div className="max-w-6xl mx-auto">
        <div ref={headerRef} className="reveal text-center max-w-2xl mx-auto">
          <p className="label-eyebrow">Sifnos · Your guide</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#4A5346] mt-6">
            Before you arrive
          </h2>
          <p className="text-[#757E70] mt-6 leading-relaxed">
            A small handbook for your trip to Sifnos — a Cycladic island of
            whitewashed villages, pottery workshops, terraced olive groves and
            a deeply traditional kitchen. Here's what we recommend to make your
            three days easy and beautiful.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {INFO_BLOCKS.map((b, i) => (
            <InfoCard key={b.key} block={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
